import {
  type ApplicationConfig,
  inject,
  provideAppInitializer,
  provideZonelessChangeDetection,
} from "@angular/core";
import { provideRouter, withComponentInputBinding, withRouterConfig } from "@angular/router";
import { provideHttpClient, withInterceptors } from "@angular/common/http";
import { MSAL_INSTANCE, MsalBroadcastService, MsalService } from "@azure/msal-angular";

import { routes } from "./app.routes";
import { authInterceptor } from "./core/interceptors/auth.interceptor";
import { MSALInstanceFactory } from "./core/auth/msal.config";
import { AUTH_PROVIDER } from "./core/auth/auth-provider.token";
import { TENANT_CONTEXT } from "./core/auth/tenant-context.token";
import { Auth0PortalAuthService } from "./core/auth/auth0-portal-auth.service";
import { EntraAuthService } from "./core/auth/entra-auth.service";
import { resolveTenantContext } from "./core/auth/resolve-tenant-context";
import { AuthStateService } from "./core/services/auth-state.service";
import { BrandingService } from "./core/services/branding.service";
import { MeService } from "./core/services/me.service";
import { environment } from "../environments/environment";

import type { IAuthProvider } from "./core/auth/interfaces";

export const appConfig: ApplicationConfig = {
  providers: [
    provideZonelessChangeDetection(),
    provideRouter(
      routes,
      withComponentInputBinding(),
      withRouterConfig({ paramsInheritanceStrategy: "always" })
    ),
    provideHttpClient(withInterceptors([authInterceptor])),

    { provide: MSAL_INSTANCE, useFactory: MSALInstanceFactory },
    MsalService,
    // MsalService resolves MsalBroadcastService at RUNTIME, not through its
    // constructor: handleRedirectObservable() ends in
    // `this.injector.get(MsalBroadcastService).resetInProgressEvent()`. Nothing
    // catches its absence at build time, so omitting it compiles, passes tests,
    // and then throws NG0201 during the app initializer -- which failed the
    // bootstrap and left a blank page.
    MsalBroadcastService,

    // The host decides the identity provider, once, at bootstrap.
    {
      provide: TENANT_CONTEXT,
      useFactory: () =>
        resolveTenantContext(window.location.hostname, {
          baseHost: environment.baseHost,
          search: window.location.search,
        }),
    },
    {
      provide: AUTH_PROVIDER,
      useFactory: (): IAuthProvider => {
        const tenant = inject(TENANT_CONTEXT);
        return tenant.kind === "auth0"
          ? inject(Auth0PortalAuthService)
          : inject(EntraAuthService);
      },
    },

    // Branding first: the custom properties must be on the document before the
    // first paint, or the portal flashes Davaco indigo before turning green.
    provideAppInitializer(() => inject(BrandingService)
.load()),
    // Restoring a session must never be able to stop the app from rendering.
    // A rejected app initializer aborts bootstrap outright, and the user gets a
    // blank page with the reason only in the console. Swallow it: the guards
    // send an unauthenticated visitor to /login, which is the correct outcome
    // for every failure this can produce.
    provideAppInitializer(() => {
      const provider = inject(AUTH_PROVIDER);
      const authState = inject(AuthStateService);
      const me = inject(MeService);

      return provider
        .initialize()
        .then(async () => {
          if (!authState.isAuthenticated()) {
            return;
          }

          // Ask the API who this is. The token's own claims are not enough:
          // an Entra id token and the API's access token carry app roles from
          // DIFFERENT registrations, so an employee can hold the admin role
          // for every request the API serves and still look role-less here.
          // Awaited in the initializer so the guards see the real answer on
          // the first navigation rather than after a flicker.
          const identity = await me.load();
          if (identity !== null) {
            authState.applyServerIdentity(identity.roles, identity.isDavacoAdmin);
          }
        })
        .catch((error: unknown) => {
          console.error("customer-portal: session restore failed", error);
          authState.clear();
        });
    }),
  ],
};
